import { useEffect, useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import { supabase } from '@/lib/supabase';

/** A short-lived link to a private photo. Photos are never public. */
export function usePhotoUrl(path: string | null | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    if (!path) { setUrl(null); return; }
    supabase.storage.from('member-photos').createSignedUrl(path, 3600).then(({ data }) => { if (live) setUrl(data?.signedUrl ?? null); });
    return () => { live = false; };
  }, [path]);
  return url;
}

export function Avatar({ path, name, size = 56 }: { path: string | null | undefined; name: string; size?: number }) {
  const url = usePhotoUrl(path);
  const initials = name.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
  return url
    ? <img src={url} alt={`Photo of ${name}`} className="flex-none rounded-sm border border-stone-200 object-cover" style={{ width: size, height: Math.round(size * 1.25) }} />
    : <span className="flex flex-none items-center justify-center rounded-sm border border-dashed border-stone-300 bg-stone-50 font-display font-bold text-ink-500" style={{ width: size, height: Math.round(size * 1.25), fontSize: size * 0.32 }} aria-hidden="true">{initials}</span>;
}

/**
 * Crops to a passport shape (4:5), scales to 480x600 and re-encodes as JPEG in
 * the browser before upload. Re-encoding drops the photo's hidden data,
 * including where it was taken.
 */
async function toPassportJpeg(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const W = 480, H = 600, target = W / H;
  let sw = bitmap.width, sh = bitmap.height, sx = 0, sy = 0;
  if (sw / sh > target) { sw = Math.round(sh * target); sx = Math.round((bitmap.width - sw) / 2); }
  else { sh = Math.round(sw / target); sy = Math.round((bitmap.height - sh) / 3); } // keep faces: crop more from the bottom
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  canvas.getContext('2d')!.drawImage(bitmap, sx, sy, sw, sh, 0, 0, W, H);
  return new Promise((resolve, reject) => canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Could not read that photo'))), 'image/jpeg', 0.85));
}

/** Add or change a person's photo. Stored privately; the account holder manages their household's photos. */
export function PhotoUpload({ personId, path, name, onChanged, size = 64 }: { personId: string; path: string | null; name: string; onChanged: () => void; size?: number }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function pick(file: File | undefined) {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const jpeg = await toPassportJpeg(file);
      const newPath = `${personId}/${Date.now()}.jpg`;
      const up = await supabase.storage.from('member-photos').upload(newPath, jpeg, { contentType: 'image/jpeg' });
      if (up.error) throw up.error;
      const { error } = await supabase.from('person').update({ photo_path: newPath }).eq('id', personId);
      if (error) throw error;
      if (path) await supabase.storage.from('member-photos').remove([path]);
      onChanged();
    } catch {
      setError('That photo could not be saved. Try a JPEG or a photo taken on your phone.');
    } finally { setBusy(false); if (input.current) input.current.value = ''; }
  }
  return (
    <div className="flex flex-none flex-col items-center gap-1">
      <Avatar path={path} name={name} size={size} />
      <button type="button" className="inline-flex items-center gap-1 text-xs font-semibold link disabled:opacity-50" disabled={busy} onClick={() => input.current?.click()}>
        <Camera size={13} /> {busy ? 'Saving…' : path ? 'Change' : 'Add photo'}
      </button>
      <input ref={input} type="file" accept="image/*" capture="user" className="sr-only" aria-label={`Photo of ${name}`} onChange={e => pick(e.target.files?.[0])} />
      {error && <p className="max-w-[10rem] text-center text-xs text-brand-700" role="alert">{error}</p>}
    </div>
  );
}
